/**
 * Os documentos que chegaram ao Caso e ainda não sustentam nenhum item (`TP-057`, `D11`, `D12`): o anexo de e-mail que a
 * leitura não reconheceu, o PDF do IW, o envio solto pela tela. A pessoa diz a qual item cada um pertence; o item vira
 * `recebido` e só fecha quando alguém o valida. O envio solto também mora aqui. O que já foi julgado e não serve (o
 * recusado, o envio errado) é DESCARTADO: sai da lista, fica recolhido em "Descartados" e volta com um clique.
 *
 * Nada sai da plataforma: não há botão que peça documento a ninguém (restrição 9).
 */

import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import { formatarDataHora } from '../../../shared/dominio/datas';
import {
  atribuirDocumento,
  chavesDeDocumentos,
  descartarDocumento,
  documentosDoCaso,
  urlDoArquivoDoDocumento,
} from './api';
import { mensagemDeErroDeDocumento, useAcoesDeDocumento } from './acoes';
import { MetadadosDoDocumento } from './MetadadosDoDocumento';
import {
  ROTULO_DA_LEITURA_DO_ARQUIVO,
  ROTULO_DA_ORIGEM,
  ROTULO_DO_MOTIVO,
  ROTULO_DO_VEREDITO,
  type DocumentoDoCaso,
  type DocumentoExigido,
} from './tipos';

export function DocumentosSoltos({ casoId }: { casoId: string }): React.JSX.Element | null {
  const consulta = useQuery(documentosDoCaso(casoId));
  const cliente = useQueryClient();
  const { enviar } = useAcoesDeDocumento(casoId);
  const seletor = useRef<HTMLInputElement>(null);

  const atribuir = useMutation({
    mutationFn: (p: { documentoId: string; exigidoId: string }) =>
      atribuirDocumento(casoId, p.documentoId, p.exigidoId),
    onSuccess: (visao) => {
      cliente.setQueryData(chavesDeDocumentos.doCaso(casoId), visao);
    },
  });

  const descartar = useMutation({
    mutationFn: (p: { documentoId: string; descartar: boolean }) =>
      descartarDocumento(casoId, p.documentoId, p.descartar),
    onSuccess: (visao) => {
      cliente.setQueryData(chavesDeDocumentos.doCaso(casoId), visao);
    },
  });
  const [verDescartados, setVerDescartados] = useState(false);

  const exigidos = consulta.data?.exigidos ?? [];
  // Sem item exigido não há onde pendurar documento: a seção nem aparece (Caso de operadora sem curadoria de documento).
  if (exigidos.length === 0) return null;

  const sustentando = new Set(exigidos.map((e) => e.documentoId).filter((id): id is string => id !== null));
  const semItem = (consulta.data?.documentos ?? []).filter((d) => !sustentando.has(d.id));
  const soltos = semItem.filter((d) => d.descartadoEm === null);
  const descartados = semItem.filter((d): d is DocumentoDoCaso & { descartadoEm: Date } => d.descartadoEm !== null);
  const pendentes = exigidos.filter((e) => e.estado === 'pendente');
  const erro = atribuir.error ?? enviar.error ?? descartar.error;

  return (
    <section
      className="overflow-hidden rounded-lg border border-solid border-[var(--fio)] bg-[var(--chao)]"
      aria-label="Documentos soltos"
    >
      <div className="flex items-center gap-2 border-0 border-b border-solid border-[var(--fio)] bg-[var(--chao-2)] px-4 py-2">
        <h3 className="m-0 flex-1 text-sm font-semibold text-[var(--tinta)]">Documentos soltos</h3>
        <input
          ref={seletor}
          type="file"
          hidden
          aria-label="Arquivo solto"
          onChange={(e) => {
            const arquivo = e.target.files?.[0];
            if (arquivo !== undefined) enviar.mutate({ arquivo });
            e.target.value = '';
          }}
        />
        <Button
          size="small"
          variant="outlined"
          disabled={enviar.isPending}
          onClick={() => {
            seletor.current?.click();
          }}
        >
          Enviar documento solto
        </Button>
      </div>

      {soltos.length === 0 ? (
        <p className="m-0 px-4 py-3 text-[0.8125rem] text-[var(--tinta-2)]">
          Nenhum documento solto. O que chega por e-mail, pelo IW ou por aqui e não é reconhecido aparece nesta lista.
        </p>
      ) : (
        <ul className="m-0 list-none px-4 py-1">
          {soltos.map((d) => (
            <DocumentoSolto
              key={d.id}
              casoId={casoId}
              documento={d}
              pendentes={pendentes}
              ocupado={atribuir.isPending || descartar.isPending}
              aoAtribuir={(exigidoId) => {
                atribuir.mutate({ documentoId: d.id, exigidoId });
              }}
              aoDescartar={() => {
                descartar.mutate({ documentoId: d.id, descartar: true });
              }}
            />
          ))}
        </ul>
      )}

      {/* Recolhido de propósito: o descartado não pede nada a ninguém, só fica à mão se alguém descartou errado. */}
      {descartados.length > 0 && (
        <div className="border-0 border-t border-solid border-[var(--fio)] px-4 py-1.5">
          <Button
            size="small"
            variant="text"
            aria-expanded={verDescartados}
            onClick={() => {
              setVerDescartados((v) => !v);
            }}
          >
            {verDescartados ? 'Esconder descartados' : `Descartados (${String(descartados.length)})`}
          </Button>
          {verDescartados && (
            <ul className="m-0 list-none p-0 pb-1" aria-label="Documentos descartados">
              {descartados.map((d) => (
                <li
                  key={d.id}
                  className="flex flex-wrap items-center gap-x-2 py-1 text-[0.8125rem] text-[var(--tinta-2)]"
                >
                  <a
                    className="text-[var(--navy)] underline-offset-2"
                    href={urlDoArquivoDoDocumento(casoId, d.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Abrir o documento ${d.nomeArquivo}`}
                  >
                    {d.nomeArquivo}
                  </a>
                  <span>descartado em {formatarDataHora(d.descartadoEm)}</span>
                  <Button
                    size="small"
                    variant="text"
                    disabled={descartar.isPending}
                    aria-label={`Trazer de volta ${d.nomeArquivo}`}
                    onClick={() => {
                      descartar.mutate({ documentoId: d.id, descartar: false });
                    }}
                  >
                    Trazer de volta
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {erro !== null && (
        <p role="alert" className="m-0 px-4 pb-3 text-[0.8125rem] text-[var(--roxo)]">
          {mensagemDeErroDeDocumento(erro)}
        </p>
      )}
    </section>
  );
}

function DocumentoSolto({
  casoId,
  documento,
  pendentes,
  ocupado,
  aoAtribuir,
  aoDescartar,
}: {
  casoId: string;
  documento: DocumentoDoCaso;
  pendentes: readonly DocumentoExigido[];
  ocupado: boolean;
  aoAtribuir: (exigidoId: string) => void;
  aoDescartar: () => void;
}): React.JSX.Element {
  const [escolhido, setEscolhido] = useState('');

  const veredito = documento.ultimoVeredito === null ? null : ROTULO_DO_VEREDITO[documento.ultimoVeredito];
  const motivo = documento.ultimoMotivo === null ? null : (ROTULO_DO_MOTIVO[documento.ultimoMotivo] ?? null);
  // O que ainda espera a leitura não se descarta: o resultado dela pode ser justamente o item que faltava.
  const podeDescartar = documento.leitura !== 'pendente';

  return (
    <li className="border-0 border-t border-solid border-[var(--fio)] py-2.5 text-[0.8125rem] first:border-t-0">
      <p className="m-0">
        <a
          className="text-[var(--navy)] underline-offset-2"
          href={urlDoArquivoDoDocumento(casoId, documento.id)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Abrir o documento ${documento.nomeArquivo}`}
        >
          <b>{documento.nomeArquivo}</b>
        </a>
        <span className="text-[var(--tinta-2)]">
          {' · '}
          {ROTULO_DA_ORIGEM[documento.origem]}
          {' · '}
          {formatarDataHora(documento.registradoEm)}
        </span>
      </p>
      {/* Uma linha só para a leitura: o veredito (com o motivo) quando já houve, ou o estado da fila quando ainda não. */}
      <p className="m-0 text-[var(--tinta-2)]">
        {veredito === null
          ? ROTULO_DA_LEITURA_DO_ARQUIVO[documento.leitura]
          : motivo === null
            ? veredito
            : `${veredito}: ${motivo}`}
      </p>
      <MetadadosDoDocumento documento={documento} />

      {(pendentes.length > 0 || podeDescartar) && (
        <div className="mt-1 flex flex-wrap items-center gap-2">
          {pendentes.length > 0 && (
            <>
              <TextField
                select
                size="small"
                label="Este documento é de"
                value={escolhido}
                onChange={(e) => {
                  setEscolhido(e.target.value);
                }}
                sx={{ minWidth: 240 }}
              >
                {pendentes.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.rotulo}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                size="small"
                variant="outlined"
                disabled={ocupado || escolhido === ''}
                onClick={() => {
                  aoAtribuir(escolhido);
                }}
              >
                Ligar ao item
              </Button>
            </>
          )}
          {podeDescartar && (
            <Button
              size="small"
              variant="text"
              color="inherit"
              disabled={ocupado}
              aria-label={`Descartar ${documento.nomeArquivo}`}
              onClick={aoDescartar}
            >
              Descartar
            </Button>
          )}
        </div>
      )}
    </li>
  );
}
